/**
 * Client-side image validation utilities for aquarium photo uploads.
 * These pure functions have no React or service dependencies — independently testable.
 */

export const ALLOWED_MIME_TYPES: ReadonlySet<string> = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

/**
 * Magic byte signatures for allowed image formats.
 * Each entry describes the bytes at a fixed offset that identify the format.
 */
export interface MagicSignature {
  readonly mimeType: string;
  readonly bytes: ReadonlyArray<number>;
  readonly offset: number;
}

export const MAGIC_SIGNATURES: ReadonlyArray<MagicSignature> = [
  // JPEG: FF D8 FF
  { mimeType: 'image/jpeg', bytes: [0xff, 0xd8, 0xff], offset: 0 },
  // PNG: 89 50 4E 47 0D 0A 1A 0A
  { mimeType: 'image/png', bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a], offset: 0 },
  // WebP: RIFF at offset 0 (52 49 46 46), then WEBP at offset 8 (57 45 42 50)
  { mimeType: 'image/webp', bytes: [0x52, 0x49, 0x46, 0x46], offset: 0 },
];

/** The WEBP sub-signature at offset 8 in the file */
const WEBP_SUB_SIGNATURE: ReadonlyArray<number> = [0x57, 0x45, 0x42, 0x50];

export interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
}

/**
 * Validates that the file's MIME type is in the allowed set.
 */
export function validateMimeType(file: File): ValidationResult {
  if (ALLOWED_MIME_TYPES.has(file.type)) {
    return { valid: true };
  }
  return {
    valid: false,
    error: `File type "${file.type}" is not allowed. Please upload a JPEG, PNG, or WebP image.`,
  };
}

/**
 * Validates that the file does not exceed MAX_FILE_SIZE_BYTES.
 */
export function validateFileSize(file: File): ValidationResult {
  if (file.size <= MAX_FILE_SIZE_BYTES) {
    return { valid: true };
  }
  const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
  return {
    valid: false,
    error: `File size ${sizeMB} MB exceeds the maximum allowed size of 10 MB.`,
  };
}

/**
 * Reads the first 12 bytes of the file and checks the magic byte signature
 * against the declared MIME type.
 *
 * This catches spoofed Content-Type headers where file data doesn't match
 * the declared type.
 */
export async function validateMagicBytes(file: File): Promise<ValidationResult> {
  let buffer: ArrayBuffer;
  try {
    buffer = await file.slice(0, 12).arrayBuffer();
  } catch {
    return { valid: false, error: 'Unable to read file contents for validation.' };
  }

  const bytes = new Uint8Array(buffer);

  if (bytes.length === 0) {
    return { valid: false, error: 'File appears to be empty.' };
  }

  if (file.type === 'image/webp') {
    // WebP: RIFF at offset 0 AND WEBP at offset 8
    if (bytes.length < 12) {
      return { valid: false, error: 'File is too small to be a valid WebP image.' };
    }

    const riffSig = MAGIC_SIGNATURES[2];
    if (!riffSig) {
      return { valid: false, error: 'Internal validation error.' };
    }

    for (let i = 0; i < riffSig.bytes.length; i++) {
      const expected = riffSig.bytes[i];
      if (expected === undefined || bytes[i] !== expected) {
        return {
          valid: false,
          error: 'File contents do not match the declared WebP format.',
        };
      }
    }

    for (let i = 0; i < WEBP_SUB_SIGNATURE.length; i++) {
      const expected = WEBP_SUB_SIGNATURE[i];
      if (expected === undefined || bytes[8 + i] !== expected) {
        return {
          valid: false,
          error: 'File contents do not match the declared WebP format.',
        };
      }
    }

    return { valid: true };
  }

  const signature = MAGIC_SIGNATURES.find((sig) => sig.mimeType === file.type);
  if (!signature) {
    return { valid: false, error: `Unsupported file type: ${file.type}` };
  }

  if (bytes.length < signature.bytes.length + signature.offset) {
    return { valid: false, error: 'File is too small to be a valid image.' };
  }

  for (let i = 0; i < signature.bytes.length; i++) {
    const expected = signature.bytes[i];
    if (expected === undefined || bytes[signature.offset + i] !== expected) {
      return {
        valid: false,
        error: `File contents do not match the declared ${file.type} format.`,
      };
    }
  }

  return { valid: true };
}

/**
 * Runs all validation checks in order: MIME type → file size → magic bytes.
 * Returns the first failing result, or { valid: true } if all pass.
 *
 * This is the primary entry point for callers — it combines all three checks.
 */
export async function validateImageFile(file: File): Promise<ValidationResult> {
  const mimeResult = validateMimeType(file);
  if (!mimeResult.valid) {
    return mimeResult;
  }

  const sizeResult = validateFileSize(file);
  if (!sizeResult.valid) {
    return sizeResult;
  }

  return validateMagicBytes(file);
}
